/**
 * Üst menü çubuğu: kök modüller yatay, klasörler açılır menü.
 *
 * Sığmayanlar eskiden yatay kaydırmaya düşüyordu — ekranın sağında kalan modüller
 * görünmüyor, kaydırma çubuğu da olmadığı için varlıkları fark edilmiyordu. Artık
 * sığmayanlar "…" menüsünde toplanıyor; menü genişliği ölçülerek hesaplanıyor.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/shared/ui/DropdownMenu';
import { useOverflowCount } from '@/shared/hooks/useOverflowCount';
import type { UserScreenNode } from '@/shared/types/system';
import { useMenu, isLeaf } from './useMenu';
import { NavIcon } from './NavIcon';
import { NavNode } from './NavNode';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useTabsStore } from '../tabs/tabsStore';

export function TopNavBar() {
  const { t } = useTranslation();
  const { tree } = useMenu();
  const openScreen = useOpenScreen();
  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const activeScreenId = activeTab?.screenId ?? null;

  const barRef = useRef<HTMLDivElement>(null);
  const fits = useOverflowCount(barRef, {
    itemCount: tree.length,
    overflowButtonWidth: 44,
    gap: 4,
    minVisible: 1,
  });
  const visible = tree.slice(0, fits);
  const overflow = tree.slice(fits);

  const renderItem = (n: UserScreenNode) =>
    isLeaf(n) ? (
      <button
        key={n.screenId}
        type="button"
        onClick={() => openScreen(n)}
        className="hover:bg-bg-hover flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm whitespace-nowrap"
      >
        <NavIcon node={n} /> {n.caption}
      </button>
    ) : (
      <DropdownMenu key={n.screenId}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="hover:bg-bg-hover data-[state=open]:bg-bg-hover flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm whitespace-nowrap"
          >
            <NavIcon node={n} /> {n.caption} <ChevronDown className="text-fg-subtle h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[70vh] w-72 overflow-y-auto">
          {n.children.map((c) => (
            <NavNode
              key={c.screenId}
              node={c}
              depth={0}
              activeScreenId={activeScreenId}
              onOpen={openScreen}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return (
    <nav aria-label={t('shell.menu')} className="flex min-w-0 flex-1 items-center gap-1">
      {/* Ölçüm kutusu: taşma hesabı bu genişliğe göre yapılır. */}
      <div ref={barRef} className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {visible.map(renderItem)}
      </div>
      {overflow.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('shell.moreModules')}
              title={t('shell.moreModules')}
              className="hover:bg-bg-hover data-[state=open]:bg-bg-hover flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="text-fg-subtle">{overflow.length}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
            {/* Taşan kökler kendi alt ağaçlarıyla birlikte iner: menüde kaybolmasınlar. */}
            {overflow.map((n) => (
              <NavNode
                key={n.screenId}
                node={n}
                depth={0}
                activeScreenId={activeScreenId}
                onOpen={openScreen}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </nav>
  );
}
